import { ReactNode, ReactElement, lazy, Suspense } from 'react';
import _ from 'lodash';
import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import { WebThingPortType } from '@API/WebNodeTypes';
import { WebThingPort, WebThing } from '@API/WebThing';
import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import * as WidgetsThingsStructs from '@UI/Widgets/Things/WidgetsThingsStructs';
import { IWidgetThingsClass } from '@UI/Widgets/Things/Utils/IWidgetThingsClass';
import { WidgetDescriptionToPlegmaThing, IWidgetDescription, IWidgetDataValidateResp } from '@UI/Widgets/WidgetStructs';
import { LazyLoadingAnimation } from '@Primitive/Lazy';
import { PortKeys, SpaceDesignerSettings } from './SpaceDesignerStructs';

// ------------------------------------------------------------------------------------------------------
const SpaceDesigner = lazy(() => import("./SpaceDesignerAsync"));
const SpaceDesignerSettings = lazy(() => import("./SpaceDesignerAsync").then(module => ({ default: module.SpaceDesignerWidgetSettings })));
// =================================================================================================
export class SpaceDesignerWidget extends IWidgetThingsClass<SpaceDesignerSettings> {
    Render(key: string, width: number, height: number): ReactNode {
        return <WidgetsThingsStructs.LazyWidget >
            <SpaceDesigner />
        </WidgetsThingsStructs.LazyWidget>
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <Suspense fallback={<LazyLoadingAnimation />}>
            <SpaceDesignerSettings
                key={'settings-' + key}
                width={width}
                height={height}
                WidgetData={_.cloneDeep(this.WidgetData)}
                WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
                ThingKey={this.ThingKey}
                NodeKey={this.NodeKey}
                onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
                SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
            />
        </Suspense>
    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(widgetData: SpaceDesignerSettings): WebThingPort[] {
        let ports: WebThingPort[] = [];

        ports.push(new WebThingPort(
            {
                Name: "GeoJSON",
                PortKey: PortKeys.GeoJSON,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(PortKeys.GeoJSON, data))
        );

        ports.push(new WebThingPort(
            {
                Name: "OnPopupDataChange",
                PortKey: PortKeys.OnPopupDataChange,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            },
            WebThingPortType.Data)
        );

        ports.push(new WebThingPort(
            {
                Name: "OnShapeSelected",
                PortKey: PortKeys.OnShapeSelected,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            },
            WebThingPortType.Data)
        );
        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let widgetData = this.WidgetData,
            webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        webThing.Ports = {};

        // Add thing's ports.
        this
            .createThingPorts(widgetData)
            .forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode
            .GetModule(this.WebModuleKey)
            .UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        let widgetData = (this.WidgetData as SpaceDesignerSettings);

        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts(widgetData);
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): SpaceDesignerSettings {
        return {
            // TODO: Set the default values for the widget
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return SpaceDesignerWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        // TODO: Update the widget Description
        return {
            Title: 'Space Designer',
            WidgetTitle: '',
            Key: 'SpaceDesignerWidget',
            Type: 'SpaceDesignerWidget',
            Data: SpaceDesignerWidget.GetDefaultWidgetDataStatic(),
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/svg/tile.svg'
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = {};

        // expose title if defined
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    }
    // ---------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        // todo: set schema validators
        return { valid: true, errors: "" };
    }
    // ---------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: SpaceDesignerSettings): SpaceDesignerSettings {
        return data;
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultValues() { return {} }
    // ---------------------------------------------------------------------------------------------
}